import { useMemo } from 'react';
import {
  getHoveredSwatchLabelClasses,
  getMoreOptionsText,
  getOptionsLinkClasses,
  getSskuSwatchContainerClasses,
  getSwatchesCount,
  getSwatchesToDisplay,
  shouldDisplaySwatches,
  shouldDisplaySwatchesSpace,
  shouldShowMoreOptionsLink
} from '../mini-swatch-utils';

/**
 * Custom hook for computing display values and styles for SuperSkuMiniSwatches
 * @param {Object} params - Hook parameters
 * @param {Object} params.state - Current reducer state
 * @param {Array} params.swatchesData - Swatches data from API
 * @param {number} params.totalNumberOfOptionsAvailable - Total options from API
 * @param {number} params.numberOfSwatchesToDisplay - Max number of swatches to display
 * @param {boolean} params.isMobile - Whether current view is mobile
 * @param {boolean} params.center - Whether to center swatches
 * @param {boolean} params.removeStaticHeight - Whether to remove static height
 * @returns {Object} Computed display values and CSS classes
 */
export const useSwatchDisplay = ({
  state,
  swatchesData,
  totalNumberOfOptionsAvailable,
  numberOfSwatchesToDisplay,
  isMobile,
  center,
  removeStaticHeight
}) => {
  const { swatches = [], totalNumberOfOptions = 0 } = state;
  const numberOfOptions = totalNumberOfOptionsAvailable || totalNumberOfOptions;
  const availableSwatches = (swatches.length ? swatches : swatchesData);

  // Memoized computed values
  const swatchesToDisplay = useMemo(() => {
    return getSwatchesToDisplay(swatches, swatchesData, numberOfSwatchesToDisplay);
  }, [swatches, swatchesData, numberOfSwatchesToDisplay]);

  const displaySwatchesSpace = shouldDisplaySwatchesSpace(swatchesToDisplay, isMobile);
  const displaySwatches = shouldDisplaySwatches(swatchesToDisplay);
  const swatchesCount = getSwatchesCount(displaySwatches, swatchesToDisplay);
  const showMoreOptionsLink = shouldShowMoreOptionsLink(
    numberOfOptions,
    availableSwatches,
    numberOfSwatchesToDisplay
  );
  const moreOptionsText = getMoreOptionsText(totalNumberOfOptionsAvailable, swatchesCount);

  // Memoized CSS classes
  const cssClasses = useMemo(() => {
    return {
      hoveredSwatchLabelClasses: getHoveredSwatchLabelClasses(removeStaticHeight),
      sskuSwatchContainerClasses: getSskuSwatchContainerClasses(center, removeStaticHeight),
      optionsLinkClasses: getOptionsLinkClasses(removeStaticHeight, isMobile)
    };
  }, [center, removeStaticHeight]);

  return {
    swatchesToDisplay,
    displaySwatchesSpace,
    displaySwatches,
    showMoreOptionsLink,
    moreOptionsText,
    ...cssClasses
  };
};
